/**
 * Utility functions for formatting file data
 */

export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Format date for display
 * - Today: "Today at 2:34 PM"
 * - Yesterday: "Yesterday"
 * - This week: "3 days ago"
 * - Older: "Jan 15" or "Jan 15, 2024" (if different year)
 */
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  
  // Check if same day
  const isToday = date.toDateString() === now.toDateString();
  
  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  
  if (isToday) {
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return `Today at ${timeStr}`;
  }
  
  if (isYesterday) {
    return 'Yesterday';
  }
  
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

/**
 * Format date with time (always includes time)
 */
export const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  
  const dateStr = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
  
  const timeStr = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  return `${dateStr} at ${timeStr}`;
};

/**
 * Format relative time between two dates
 * Returns human-readable string like "3 days older" or "2 hours older"
 */
export const formatRelativeTime = (olderDateString, newerDateString) => {
  const older = new Date(olderDateString);
  const newer = new Date(newerDateString);
  const diffMs = newer - older;
  
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} older`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} older`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} older`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} older`;
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} older`;
};
